import React from "react";
import { Header, MetricEdgeProto } from "build-ts/metadata";
import { 
  Box, 
  Container, 
  HStack, 
  Text, 
  VStack,
  Button,
  Checkbox,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
} from "@chakra-ui/react";
import { CreateOrUpdateMetrics_Request } from "build-ts/interface";
import { DeleteIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { ColumnType, MetricEdgeType } from "build-ts/enums";
import { FaChartPie } from "react-icons/fa";
import { MdBarChart, MdNumbers } from "react-icons/md";
import { PiTarget } from "react-icons/pi";
import { FiCalendar } from "react-icons/fi";

interface EditMKGProps {
  metricInfo: CreateOrUpdateMetrics_Request | null;
  allMetrics: Header[] | null;
  allBenchmarks: Header[] | null;
  allKGColumns: Header[] | null;

  metricEdge: MetricEdgeProto[] | null;
  benchmarkEdge: MetricEdgeProto[] | null;
  kgColumnEdge: MetricEdgeProto[] | null;

  selectedMetrics: Header[];
  selectedBenchmarks: Header[];
  selectedAttributes: Header[];
  selectedMeasures: Header[];
  selectedTemporals: Header[];

  onMetricChange: (metric: Header, checked: boolean) => void;
  onBenchmarkChange: (benchmark: Header, checked: boolean) => void;
  onAttributeChange: (attribute: Header, checked: boolean) => void;
  onMeasureChange: (measure: Header, checked: boolean) => void;
  onTemporalChange: (temporal: Header, checked: boolean) => void;

  handleRemoveBenchmarkEdge: (index: number) => void;
  handleRemoveMetricEdge: (index: number) => void;
  handleRemoveKgColumnEdge: (id: string) => void;

  handleSubmit: () => void;
}

const EditMKGComponent: React.FC<EditMKGProps> = ({ 
  metricInfo,
  allMetrics,
  allBenchmarks,
  allKGColumns,

  metricEdge,
  benchmarkEdge,
  kgColumnEdge,
  
  selectedMetrics,
  selectedBenchmarks,
  selectedAttributes,
  selectedMeasures,
  selectedTemporals,
  
  onMetricChange,
  onBenchmarkChange,
  onAttributeChange,
  onMeasureChange,
  onTemporalChange,

  handleRemoveBenchmarkEdge,
  handleRemoveMetricEdge,
  handleRemoveKgColumnEdge,
  handleSubmit
}) => {
  return (
    <Container maxW="container.xl">
      <VStack align="stretch" spacing={4}>
        <HStack>
          {/* Related Metric */}
          <Box
            flex="1"
            border="2px"
            borderColor="purple.700"
            borderRadius="xl"
            bg="white"
            boxShadow="md"
            alignSelf="stretch"
            display="flex"
            flexDirection="column"
          >
            {/* Header */}
            <HStack spacing={3} p={4} bg="purple.700" borderTopRadius="md">
              <Icon as={MdBarChart} boxSize={6} color="white" />
              <Text fontSize="md" fontWeight="700" mb={0} color="white">
                Metrics
              </Text>
            </HStack>
            
            <VStack align="stretch" spacing={3} p={4} flex="1">             
              {metricEdge && metricEdge.map((option, index) => (
                 <HStack
                 key={index}
                 bg="purple.50"
                 borderWidth="1px"
                 borderRadius="lg"
                 borderColor="purple.700"
                 px={2}
                 justifyContent="space-between"
               >
                 <Text fontWeight={600} fontSize="sm" mb={0} color="purple.800">
                   {option.benchmark?.header?.name ?? option?.header?.name}
                 </Text>
                 <Button
                   variant="ghost"
                   size="sm"
                   leftIcon={<DeleteIcon boxSize="0.8rem" />}
                   color="gray.600"
                   onClick={() => handleRemoveMetricEdge(index)}
                   _hover={{ bg: "red.50", color: "red.600" }}
                   iconSpacing={0}
                 />
               </HStack>
              ))}
            </VStack>
            <Box p={4}>
              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  w="100%"
                  variant="outline"
                  borderColor="purple.700"
                  _hover={{ bg: "purple.50", borderColor: "purple.800" }}
                  _active={{ bg: "purple.50" }}
                  color="purple.700"
                  fontWeight="600"
                >
                  <Text fontSize="sm" mb={0} color={selectedMetrics.length > 0 ? "purple.700" : "purple.700"}>
                    {selectedMetrics.length > 0 
                      ? `${selectedMetrics.length} metric${selectedMetrics.length > 1 ? "s" : ""} selected` 
                      : "Select metrics"}
                  </Text>
                </MenuButton>
                <MenuList maxH="200px" overflowY="auto">
                  {allMetrics && (() => {
                    const filteredMetrics = allMetrics
                      .filter((metric) =>
                        metricEdge
                          ? !metricEdge.find(
                            (option) => option.header?.id === metric.id
                          )
                          : true
                      )
                      .filter((metric) => metric.id !== metricInfo?.metrics[0]?.header?.id);
                    
                    return filteredMetrics.length > 0 ? filteredMetrics.map(metric => (
                      <MenuItem
                        key={metric.id}
                        onClick={() => onMetricChange(metric, !selectedMetrics.some(selected => selected.id === metric.id))}
                        fontSize="sm"
                      >
                        <Checkbox
                          isChecked={selectedMetrics.some(selected => selected.id === metric.id)}
                          onChange={(e) => onMetricChange(metric, e.target.checked)}
                          mr={2}
                          colorScheme="purple"
                          size="sm"
                        />
                        {metric.name}
                      </MenuItem>
                    )) : (
                      <MenuItem isDisabled fontSize="sm" color="gray.500">
                        No metrics available
                      </MenuItem>
                    );
                  })()}
                </MenuList>
              </Menu>
            </Box>
          </Box>
          {/* Related Benchmark */}
          <Box
            flex="1"
            border="2px"
            borderColor="orange.500"
            borderRadius="xl"
            bg="white"
            boxShadow="md"
            alignSelf="stretch"
            display="flex"
            flexDirection="column"
          >
            {/* Header */}
            <HStack spacing={3} p={4} bg="orange.500" borderTopRadius="md">
              <Icon as={PiTarget} boxSize={6} color="white" />
              <Text fontSize="md" fontWeight="700" mb={0} color="white">
                Benchmarks
              </Text>
            </HStack>
            
            <VStack align="stretch" spacing={3} p={4} flex="1">             
              {benchmarkEdge && benchmarkEdge.map((option, index) => (
                <HStack
                  key={index}
                  bg="orange.50"
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor="orange.500"
                  px={2}
                  justifyContent="space-between"
                >
                  <Text fontWeight={600} fontSize="sm" mb={0} color="orange.800">
                    {option.benchmark?.header?.name ?? option?.header?.name}
                  </Text>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<DeleteIcon boxSize="0.8rem" />}
                    color="gray.600"
                    onClick={() => handleRemoveBenchmarkEdge(index)}
                    _hover={{ bg: "red.50", color: "red.600" }}
                    iconSpacing={0}
                  />
                </HStack>
              ))}
            </VStack>
            
            <Box p={4}>
              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  w="100%"
                  variant="outline"
                  borderColor="orange.500"
                  _hover={{ bg: "orange.50", borderColor: "orange.600" }}
                  _active={{ bg: "orange.50" }}
                  color="orange.600"
                  fontWeight="600"
                >
                  <Text fontSize="sm" mb={0} color={selectedBenchmarks.length > 0 ? "orange.600" : "orange.600"}>
                    {selectedBenchmarks.length > 0 
                      ? `${selectedBenchmarks.length} benchmark${selectedBenchmarks.length > 1 ? "s" : ""} selected` 
                      : "Select benchmarks"}
                  </Text>
                </MenuButton>
                <MenuList maxH="200px" overflowY="auto">
                  {allBenchmarks && (() => {
                    const filteredBenchmarks = allBenchmarks
                      .filter((benchmark) =>
                        benchmarkEdge
                          ? !benchmarkEdge.find(
                            (option) => option?.header?.id === benchmark.id
                          )
                          : true
                      );
                    
                    return filteredBenchmarks.length > 0 ? filteredBenchmarks.map(benchmark => (
                      <MenuItem
                        key={benchmark.id}
                        onClick={() => onBenchmarkChange(benchmark, !selectedBenchmarks.some(selected => selected.id === benchmark.id))}
                        fontSize="sm"
                      >
                        <Checkbox
                          isChecked={selectedBenchmarks.some(selected => selected.id === benchmark.id)}
                          onChange={(e) => onBenchmarkChange(benchmark, e.target.checked)}
                          mr={2}
                          colorScheme="orange"
                          size="sm"
                        />
                        {benchmark.name}
                      </MenuItem>
                    )) : (
                      <MenuItem isDisabled fontSize="sm" color="gray.500">
                        No benchmarks available
                      </MenuItem>
                    );
                  })()}
                </MenuList>
              </Menu>
            </Box>
          </Box>
          {/* Related Attribute */}
          <Box
            flex="1"
            border="2px"
            borderColor="blue.600"
            borderRadius="xl"
            bg="white"
            boxShadow="md"
            alignSelf="stretch"
            display="flex"
            flexDirection="column"
          >
            {/* Header */}
            <HStack spacing={3} p={4} bg="blue.600" borderTopRadius="md">
              <Icon as={FaChartPie} boxSize={6} color="white" />
              <Text fontSize="md" fontWeight="700" mb={0} color="white">
                Attributes
              </Text>
            </HStack>
            
            <VStack align="stretch" spacing={3} p={4} flex="1">             
              {kgColumnEdge && 
                kgColumnEdge.filter((edge) => edge.edge_type === MetricEdgeType.METRIC_ATTRIBUTE_EDGE).map((option, index) => (
                  <HStack
                    key={index}
                    bg="blue.50"
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor="blue.600"
                    px={2}
                    justifyContent="space-between"
                  >
                    <Text fontWeight={600} fontSize="sm" mb={0} color="blue.800">
                      {option?.header?.name}
                    </Text>
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<DeleteIcon boxSize="0.8rem" />}
                      color="gray.600"
                      onClick={() => handleRemoveKgColumnEdge(option?.header?.id!)}
                      _hover={{ bg: "red.50", color: "red.600" }}
                      iconSpacing={0}
                    />
                  </HStack>
                ))
              }
            </VStack>
            
            <Box p={4}>
              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  w="100%"
                  variant="outline"
                  borderColor="blue.600"
                  _hover={{ bg: "blue.50", borderColor: "blue.700" }}
                  _active={{ bg: "blue.50" }}
                  color="blue.600"
                  fontWeight="600"
                >
                  <Text fontSize="sm" mb={0} color={selectedAttributes.length > 0 ? "blue.600" : "blue.600"}>
                    {selectedAttributes.length > 0 
                      ? `${selectedAttributes.length} attribute${selectedAttributes.length > 1 ? "s" : ""} selected` 
                      : "Select attributes"}
                  </Text>
                </MenuButton>
                <MenuList maxH="200px" overflowY="auto">
                  {allKGColumns && (() => {
                    const filteredAttributes = allKGColumns
                      .filter((column) =>
                        kgColumnEdge
                          ? !kgColumnEdge.find(
                            (option) => option.header?.id === column.id
                          )
                          : []
                      )
                      .filter((column) => column.column_type === ColumnType.ATTRIBUTE);
                    
                    return filteredAttributes.length > 0 ? filteredAttributes.map((column) => (
                      <MenuItem
                        key={column.id}
                        onClick={() => onAttributeChange(column, !selectedAttributes.some(selected => selected.id === column.id))}
                        fontSize="sm"
                      >
                        <Checkbox
                          isChecked={selectedAttributes.some(selected => selected.id === column.id)}
                          onChange={(e) => onAttributeChange(column, e.target.checked)}
                          mr={2}
                          colorScheme="blue"
                          size="sm"
                        />
                        {column.name}
                      </MenuItem>
                    )) : (
                      <MenuItem isDisabled fontSize="sm" color="gray.500">
                        No attributes columns available
                      </MenuItem>
                    );
                  })()}
                </MenuList>
              </Menu>
            </Box>
          </Box>
          {/* Related Measure */}
          <Box
            flex="1"
            border="2px"
            borderColor="green.600"
            borderRadius="xl"
            bg="white"
            boxShadow="md"
            alignSelf="stretch"
            display="flex"
            flexDirection="column"
          >
            {/* Header */}
            <HStack spacing={3} p={4} bg="green.600" borderTopRadius="md">
              <Icon as={MdNumbers} boxSize={6} color="white" />
              <Text fontSize="md" fontWeight="700" mb={0} color="white">
                Measures
              </Text>
            </HStack>
            
            <VStack align="stretch" spacing={3} p={4} flex="1">             
              {kgColumnEdge && 
                kgColumnEdge.filter((edge) => edge.edge_type === MetricEdgeType.METRIC_MEASURE_EDGE).map((option, index) => (
                  <HStack
                    key={index}
                    bg="green.50"
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor="green.600"
                    px={2}
                    justifyContent="space-between"
                  >
                    <Text fontWeight={600} fontSize="sm" mb={0} color="green.800">
                      {option?.header?.name}
                    </Text>
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<DeleteIcon boxSize="0.8rem" />}
                      color="gray.600"
                      onClick={() => handleRemoveKgColumnEdge(option?.header?.id!)}
                      _hover={{ bg: "red.50", color: "red.600" }}
                      iconSpacing={0}
                    />
                  </HStack>
                ))
              }
            </VStack>
            
            <Box p={4}>
              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  w="100%"
                  variant="outline"
                  borderColor="green.600"
                  _hover={{ bg: "green.50", borderColor: "green.700" }}
                  _active={{ bg: "green.50" }}
                  color="green.600"
                  fontWeight="600"
                >
                  <Text fontSize="sm" mb={0} color={selectedMeasures.length > 0 ? "green.600" : "green.600"}>
                    {selectedMeasures.length > 0 
                      ? `${selectedMeasures.length} measure${selectedMeasures.length > 1 ? "s" : ""} selected` 
                      : "Select measures"}
                  </Text>
                </MenuButton>
                <MenuList maxH="200px" overflowY="auto">
                  {allKGColumns && (() => {
                    const filteredMeasures = allKGColumns
                      .filter((column) =>
                        kgColumnEdge
                          ? !kgColumnEdge.find(
                            (option) => option.header?.id === column.id
                          )
                          : []
                      )
                      .filter((column) => column.column_type === ColumnType.MEASURE);
                    
                    return filteredMeasures.length > 0 ? filteredMeasures.map((column) => (
                      <MenuItem
                        key={column.id}
                        onClick={() => onMeasureChange(column, !selectedMeasures.some(selected => selected.id === column.id))}
                        fontSize="sm"
                      >
                        <Checkbox
                          isChecked={selectedMeasures.some(selected => selected.id === column.id)}
                          onChange={(e) => onMeasureChange(column, e.target.checked)}
                          mr={2}
                          colorScheme="green"
                          size="sm"
                        />
                        {column.name}
                      </MenuItem>
                    )) : (
                      <MenuItem isDisabled fontSize="sm" color="gray.500">
                        No measures columns available
                      </MenuItem>
                    );
                  })()}
                </MenuList>
              </Menu>
            </Box>
          </Box>
          {/* Related Temporal */}
          <Box
            flex="1"
            border="2px"
            borderColor="yellow.600"
            borderRadius="xl"
            bg="white"
            boxShadow="md"
            alignSelf="stretch"
            display="flex"
            flexDirection="column"
          >
            {/* Header */}
            <HStack spacing={3} p={4} bg="yellow.600" borderTopRadius="md">
              <Icon as={FiCalendar} boxSize={6} color="white" />
              <Text fontSize="md" fontWeight="700" mb={0} color="white">
                Datetime
              </Text>
            </HStack>
            
            <VStack align="stretch" spacing={3} p={4} flex="1">             
              {kgColumnEdge && 
                kgColumnEdge.filter((edge) => edge.edge_type === MetricEdgeType.METRIC_TIMEFRAME_EDGE).map((option, index) => (
                  <HStack
                    key={index}
                    bg="yellow.50"
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor="yellow.600"
                    px={2}
                    justifyContent="space-between"
                  >
                    <Text fontWeight={600} fontSize="sm" mb={0} color="yellow.800">
                      {option?.header?.name}
                    </Text>
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<DeleteIcon boxSize="0.8rem" />}
                      color="gray.600"
                      onClick={() => handleRemoveKgColumnEdge(option?.header?.id!)}
                      _hover={{ bg: "red.50", color: "red.600" }}
                      iconSpacing={0}
                    />
                  </HStack>
                ))
              }
            </VStack>
            
            <Box p={4}>
              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  w="100%"
                  variant="outline"
                  borderColor="yellow.600"
                  _hover={{ bg: "yellow.50", borderColor: "yellow.700" }}
                  _active={{ bg: "yellow.50" }}
                  color="yellow.700"
                  fontWeight="600"
                >
                  <Text fontSize="sm" mb={0} color={selectedTemporals.length > 0 ? "yellow.700" : "yellow.700"}>
                    {selectedTemporals.length > 0 
                      ? `${selectedTemporals.length} datetime item${selectedTemporals.length > 1 ? "s" : ""} selected` 
                      : "Select datetime"}
                  </Text>
                </MenuButton>
                <MenuList maxH="200px" overflowY="auto">
                  {allKGColumns && (() => {
                    const filteredTemporals = allKGColumns
                      .filter((column) =>
                        kgColumnEdge
                          ? !kgColumnEdge.find(
                            (option) => option.header?.id === column.id
                          )
                          : []
                      )
                      .filter((column) => column.column_type === ColumnType.TEMPORAL);
                    
                    return filteredTemporals.length > 0 ? filteredTemporals.map((column) => (
                      <MenuItem
                        key={column.id}
                        onClick={() => onTemporalChange(column, !selectedTemporals.some(selected => selected.id === column.id))}
                        fontSize="sm"
                      >
                        <Checkbox
                          isChecked={selectedTemporals.some(selected => selected.id === column.id)}
                          onChange={(e) => onTemporalChange(column, e.target.checked)}
                          mr={2}
                          colorScheme="yellow"
                          size="sm"
                        />
                        {column.name}
                      </MenuItem>
                    )) : (
                      <MenuItem isDisabled fontSize="sm" color="gray.500">
                        No temporal columns available
                      </MenuItem>
                    );
                  })()}
                </MenuList>
              </Menu>
            </Box>
          </Box>
        </HStack>
        {/* <Box bg="white" mt={4} mb={4}>
          <Button
            colorScheme="green"
            onClick={handleSubmit}
            w="100%"
            fontWeight="bold"
            fontSize="lg"
            leftIcon={<FaCheckCircle />}
          >
            Save
          </Button>
        </Box> */}
      </VStack>
    </Container>
  );
};

export default EditMKGComponent;